import _, { has } from 'lodash'
import { complaintsQuestionnaire, dailyQuestionnaires, dataTypes, OSTRC_H_ANSWERS_MAP, OSTRC_H_QUESTIONS_MAP, OSTRC_INTRO_ANSWERS_MAP, OSTRC_LETTER_TO_BODY_PART, OSTRC_O_ANSWERS_MAP, OSTRC_O_QUESTIONS_MAP, trainingLog } from '../Constants'
import { FALLBACK_LOCALE } from '../Locales'
import { nanToZero } from '../Math'
import { formatDateTime, formatDate } from '../Utils'
import I18n from 'i18n'
import moment from 'moment'

const QUESTION_TYPE_CHECKBOX = 'checkbox'
const QUESTION_TYPE_DATE = 'date'
const QUESTION_TYPE_DATE_AND_TIME = 'date_and_time'
const QUESTION_TYPE_TIME = 'time'
const QUESTION_TYPE_NUMBER = 'number'
const QUESTION_TYPE_RANGE = 'range'
const QUESTION_TYPE_RADIO = 'radio'
const QUESTION_TYPE_LIKERT = 'likert'
const QUESTION_TYPE_DROPDOWN = 'dropdown'
const QUESTION_TYPE_EXPANDABLE = 'expandable'

const PROP_TITLE = 'title'
const PROP_VALUE = 'value'
const PROP_RAW_VALUE = 'raw.value'
const PROP_RAW_TITLE = 'raw.title'
const PROP_OTHERWISE_LABEL = 'otherwise_label'

const DEFAULT_OTHERWISE_LABEL = 'otherwise'
const DEFAULT_OTHER_LABEL = 'Other'
const LIST_SEPARATOR = ', '

const localized = (text, locale) => {
  if (text == null || typeof text !== 'object') return text
  return text[locale] || text[FALLBACK_LOCALE] || _.values(text)[0]
}

export const getQuestionnaireValues = (questionnaire) => {
  return questionnaire?.data_rows?.[0]?.values
}

export const getWellnessScore = (dailyQuestionnaireValues) => {
  const sleep = parseFloat(dailyQuestionnaireValues.v1)
  const fatigue = parseFloat(dailyQuestionnaireValues.v3)
  const stress = parseFloat(dailyQuestionnaireValues.v4)
  const soreness = parseFloat(dailyQuestionnaireValues.v5)
  const mood = parseFloat(dailyQuestionnaireValues.v6)
  const wellnessScore = (nanToZero(sleep) +
    nanToZero(fatigue) +
    nanToZero(stress) +
    nanToZero(soreness) +
    nanToZero(mood)) / 25 * 100
  return wellnessScore
}

export const isQuestionnaire = (vdo) => {
  return vdo?.data_type?.data_type === dataTypes.questionnaire_type
}

// legacy function: uses old object structure
// new version in : QuestionnaireUtilsTS.tsx
export const isDailyQuestionnaire = questionnaire => {
  return dailyQuestionnaires.includes(questionnaire?.data_rows?.[0]?.questionnaire?.key)
}

// legacy function: uses old object structure
// new version in : QuestionnaireUtilsTS.tsx
export const isTrainingLog = questionnaire => {
  return questionnaire?.data_rows?.[0]?.questionnaire?.key === trainingLog &&
    questionnaire.data_rows[0].values?.v1 && questionnaire.data_rows[0].values.v1.length > 0 &&
    questionnaire.data_rows[0].values?.v2 && questionnaire.data_rows[0].values.v2.length > 0
}

export const isComplaintsQuestionnaire = questionnaire => {
  return questionnaire?.data_rows?.[0]?.questionnaire?.key?.includes(complaintsQuestionnaire)
}

export const readDailyQuestionnaire = (questionnaireValues) => {
  const parsedData = {}
  if (questionnaireValues) {
    parsedData.sleepQuality = questionnaireValues.v1
    parsedData.sleepDuration = {
      hours: questionnaireValues.v2_uren,
      minutes: questionnaireValues.v2_minuten
    }
    parsedData.fatigue = questionnaireValues.v3
    parsedData.stress = questionnaireValues.v4
    parsedData.generalMusclePain = questionnaireValues.v5
    parsedData.mood = questionnaireValues.v6
    parsedData.readiness = questionnaireValues.v7
    parsedData.restingHr = questionnaireValues.v8
    parsedData.isSick = ['yes', 'ja'].includes(questionnaireValues.v9)
    parsedData.isInjured = ['yes', 'ja'].includes(questionnaireValues.v10)
    parsedData.comments = questionnaireValues.v11
    parsedData.wellnessScore = getWellnessScore(questionnaireValues)
  }
  return parsedData
}

export const readTrainingQuestionnaire = (questionnaireValues) => {
  const readData = {}
  if (questionnaireValues) {
    readData.training_type = questionnaireValues.v1
    readData.session_type = questionnaireValues.v2
    readData.duration = questionnaireValues.v5 * 60 // Transform min into sec, for consistency
    readData.startedAt = {
      date: questionnaireValues.v3,
      hours: questionnaireValues.v4_uur,
      minutes: questionnaireValues.v4_minuten
    }
    readData.rpe = questionnaireValues.v6
    readData.satisfaction = questionnaireValues.v7
    readData.comments = questionnaireValues.v8
    readData.fun = questionnaireValues.v9
  }
  return readData
}

const readComplaintsHealthSection = (questionnaireValues) => {
  const filteredValues = _.pickBy(questionnaireValues, (value, key) => {
    return key.match(/^v[1-7]/) && !key.match(/timing$/)
  })

  const parsedValues = {}
  _.entries(filteredValues).forEach(([key, value]) => {
    const questionNumber = key[1]
    const parsedQuestion = OSTRC_H_QUESTIONS_MAP[questionNumber]

    if (questionNumber === '6') {
      const questionParameters = key.split(/_(.+)/) // question = 'v<question_number>_<extra_stuff>'
      const symptomId = questionParameters[1]
      let parsedAnswer = ''
      if (symptomId !== undefined) {
        parsedAnswer = OSTRC_H_ANSWERS_MAP[questionNumber][symptomId]
        if (parsedAnswer !== 'other') { // When the user picks "Other, namely: ___", then we need to handle it differently: we have to check for the "text" key, which has the user-filled symptom
          if (symptomId.includes('text')) { // The text field of the "Other" option
            parsedValues[parsedQuestion] = _.union(parsedValues[parsedQuestion], [value], [])
          } else {
            parsedValues[parsedQuestion] = _.union(parsedValues[parsedQuestion], [parsedAnswer], [])
          }
        }
      }
    } else { // Question 6 is a checkbox, so we need to handle it differently
      const parsedAnswer = OSTRC_H_ANSWERS_MAP[questionNumber][value]
      parsedValues[parsedQuestion] = parsedAnswer
    }
  })

  parsedValues.score = parseInt(questionnaireValues.s_h_1)

  return parsedValues
}

const readComplaintsInjurySection = (questionnaireValues) => {
  const filteredValues = _.pickBy(questionnaireValues, (value, key) => {
    //  This regex will not work on safari. Left here as a reminder
    // return key.match(/^v_o_[a-z]_\d+[a-z]?(_[a-z]+)*(?<!_timing)$/)
    return key.match(/^v_o_[a-z]_\d+[a-z]?(_[a-z]+)*/) && !key.match(/timing$/)
  })

  const parsedValues = {}
  _.entries(filteredValues).forEach(([key, value]) => {
    const questionParameters = key.split('_') // question = 'v_o_<letter>_<question_number>'

    // Parse the body part
    const bodyLetter = questionParameters[2]
    const parsedBodyPart = OSTRC_LETTER_TO_BODY_PART[bodyLetter]

    // Parse the questions and answer into something understandable
    const questionId = questionParameters[3]
    const parsedQuestion = OSTRC_O_QUESTIONS_MAP[questionId]
    const parsedAnswer = parseOstrcOAnswers(questionId, value)

    // If 9a exists, then the answer to 9 is true
    if (questionId === '9a') {
      parsedValues[parsedBodyPart][OSTRC_O_QUESTIONS_MAP[9]] = true
    }

    // Add the parsed question/answer to the section of the dataset relative to that body part
    parsedValues[parsedBodyPart] = _.assign(parsedValues[parsedBodyPart], { [parsedQuestion]: parsedAnswer })
  })

  const otherBodyPart = questionnaireValues.v_o_i_0
  if (otherBodyPart) {
    parsedValues.other.bodyPart = otherBodyPart
  }

  _.entries(parsedValues).forEach(([bodyPart, answers]) => {
    answers.score = _.sum([
      answers.participationDifficulty,
      answers.trainingAdjustment,
      answers.performanceInfluence,
      answers.painAmount
    ])
  })

  return parsedValues
}

const parseOstrcOAnswers = (questionId, answer) => {
  switch (questionId) {
    case '1':
    case '2':
    case '3':
    case '4':
    case '5':
    case '6':
    case '8':
    case '12':
      return OSTRC_O_ANSWERS_MAP[questionId][answer]
    case '13':
    case '7':
      return OSTRC_O_ANSWERS_MAP[questionId][answer] || answer // In the case they filled in the "Others" field
    case '9':
      return !answer // true = "I dont want to share", so sharedTheCauseOfInjury = false
    case '9a':
    case '10':
    case '11':
      return answer
    default:
      return undefined
  }
}

export const readComplaintsQuestionnaire = (questionnaireValues) => {
  const readData = {}
  if (questionnaireValues) {
    readData.complaints = OSTRC_INTRO_ANSWERS_MAP[questionnaireValues.v0] || OSTRC_INTRO_ANSWERS_MAP[questionnaireValues.v_o_1] || null
    readData.illness = null
    readData.injury = null
    readData.hoursOfExerciseLastWeek = parseInt(questionnaireValues.v_o_3)

    if (readData?.complaints?.includes('illness')) {
      readData.illness = readComplaintsHealthSection(questionnaireValues)
    }

    if (readData?.complaints?.includes('injury')) { // Parse the big list of questions
      readData.injury = readComplaintsInjurySection(questionnaireValues)
    }
  }

  return readData
}

const cleanupString = (str) => {
  if (!str || typeof (str) !== 'string') return str

  return str.replace(/<br ?\/>/gi, '  ').replace(/(<([^>]+)>)/gi, '').replace(/&hellip;/gi, '...')
}

export const readGenericQuestionnaire = (questionnaireData, questionnaireDefinition, locale) => {
  // Helper to mirror the update_id in the u-can-act repo for expandable subquestions
  const expandSubQuestionIdExpandable = (id, subId) => {
    if (!id) return null
    const idStr = String(id)
    if (!idStr.includes('_')) {
      return `${idStr}_${subId}`
    }
    const firstUnderscore = idStr.indexOf('_')
    return `${idStr.slice(0, firstUnderscore)}_${subId}${idStr.slice(firstUnderscore)}`
  }

  // Idify function is used in checkbox generator in the u-can-act repo, it joins strings into a safe id format with underscores.
  const idifyCheckbox = (str) => {
    if (str == null) return ''
    return String(str)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  const getOptionToken = (option) => {
    if (has(option, PROP_VALUE)) return option.value
    if (has(option, PROP_RAW_VALUE)) return option.raw.value
    if (has(option, PROP_RAW_TITLE)) return option.raw.title
    if (has(option, PROP_TITLE)) return _.get(option.title, locale) || _.values(option.title)[0]
    if (typeof option === 'string') return option
    return _.values(option)[0]
  }

  const buildCheckboxElemId = (baseId, option) => {
    const token = getOptionToken(option)
    return `${idifyCheckbox(baseId)}_${idifyCheckbox(token)}`
  }

  // Build the elem id for the otherwise option, this is currently not being used, but kept for the future cases.
  const buildCheckboxOtherwiseElemId = (baseId, otherwiseLabel = DEFAULT_OTHERWISE_LABEL) => {
    return `${idifyCheckbox(baseId)}_${idifyCheckbox(otherwiseLabel)}`
  }

  // Helper functions defined at the top so they're accessible throughout
  const getQuestionName = (question) => {
    const title = localized(question.title, locale)
    switch (question.type) {
      case QUESTION_TYPE_RANGE:
        return `${title} (${parseFloat(question.min).toFixed(1)} - ${parseFloat(question.max).toFixed(1)})`
      default:
        return title
    }
  }

  function getQuestionValue (question) {
    switch (question.type) {
      case QUESTION_TYPE_CHECKBOX: {
        const checkboxAnswer = _.get(questionnaireData.values, question.id)

        if (Array.isArray(checkboxAnswer)) {
          const validAnswers = checkboxAnswer.filter(item => item !== null && item !== undefined && item !== '')
          return validAnswers.join(LIST_SEPARATOR)
        }
        if (typeof checkboxAnswer === 'object' && checkboxAnswer !== null) {
          const selectedOptions = Object.keys(checkboxAnswer).filter(key => checkboxAnswer[key])
          return selectedOptions.join(LIST_SEPARATOR)
        }

        if (typeof checkboxAnswer === 'string' || typeof checkboxAnswer === 'number') {
          if (checkboxAnswer === '' || checkboxAnswer === null || checkboxAnswer === undefined) {
            return ''
          }
          if (Array.isArray(question.options)) {
            const matchingOption = question.options.find((opt) => {
              const optValue = _.get(opt, PROP_VALUE) || _.get(opt, PROP_RAW_VALUE)
              const optTitle = _.get(opt, `${PROP_TITLE}.${locale}`) || _.get(opt, PROP_TITLE) || opt
              const optTitleStr = typeof optTitle === 'string' ? optTitle : _.values(optTitle)[0]
              return optValue === checkboxAnswer || optTitleStr === checkboxAnswer || opt === checkboxAnswer
            })
            if (matchingOption) {
              const labelObj = _.get(matchingOption, PROP_TITLE, matchingOption)
              return localized(labelObj, locale)
            }
          }
          return String(checkboxAnswer)
        }

        const isCheckBoxSelected = (question, option) => {
          const elemId = buildCheckboxElemId(question.id, option)
          return !!_.get(questionnaireData.values, elemId)
        }

        const getOptionLabel = (option) => {
          const labelObj = _.get(option, PROP_TITLE, option)
          return localized(labelObj, locale)
        }

        const questionOptions = question.options || []
        const selectedLabels = questionOptions.filter((option) => isCheckBoxSelected(question, option)).map((option) => getOptionLabel(option))

        // Handle otherwise checkbox, for future cases.
        if (has(question, PROP_OTHERWISE_LABEL)) {
          const otherwiseId = buildCheckboxOtherwiseElemId(question.id, _.get(question, ['raw', PROP_OTHERWISE_LABEL], question.otherwise_label))
          if (_.get(questionnaireData.values, otherwiseId)) {
            selectedLabels.push(_.get(question, PROP_OTHERWISE_LABEL, DEFAULT_OTHER_LABEL))
          }
        }
        return selectedLabels.join(LIST_SEPARATOR)
      }
      case QUESTION_TYPE_DATE: {
        const dateAnswer = _.get(questionnaireData.values, question.id)
        return formatDate(dateAnswer)
      }
      case QUESTION_TYPE_DATE_AND_TIME: {
        const dateAnswer = _.get(questionnaireData.values, question.id, '')
        const hoursAnswer = _.get(questionnaireData.values, question.hours_id, '00')
        const minutesAnswer = _.get(questionnaireData.values, question.minutes_id, '00')
        return formatDateTime(`${dateAnswer} ${hoursAnswer}:${minutesAnswer}`)
      }
      case QUESTION_TYPE_TIME: {
        const hoursAnswer = _.get(questionnaireData.values, `${question.id}_uren`)
        const hoursUnit = localized(question.hours_label, locale) || 'h'
        const minutesAnswer = _.get(questionnaireData.values, `${question.id}_minuten`)
        const minutesUnit = localized(question.minutes_label, locale) || 'm'
        return `${hoursAnswer} ${hoursUnit} ${minutesAnswer} ${minutesUnit}`
      }
      case QUESTION_TYPE_NUMBER: {
        const numberAnswer = _.get(questionnaireData.values, question.id)
        return parseFloat(numberAnswer)
      }
      case QUESTION_TYPE_RANGE: {
        const rangeAnswer = _.get(questionnaireData.values, question.id)
        return parseFloat(rangeAnswer).toFixed(1)
      }
      case QUESTION_TYPE_RADIO:
      case QUESTION_TYPE_LIKERT: {
        // Support that the otherwise label may be used as one of the options
        // WARNING: Currently, the platform stores the otherwise label as the answer, and not the otherwise value written in the text box.
        // This code works for this situation, but if it changes, then the code must also take into account the otherwise value.
        const extendedQuestionOptions = [...question.options]
        if (question.otherwise_label) extendedQuestionOptions.push(question.otherwise_label)

        const isString = (option) => typeof option === 'string' || option instanceof String

        // Question options come in several shapes. Return the values that should be compared
        // against the stored answer for a single option.
        const getOptionMatchValues = (option) => {
          if (has(option, PROP_VALUE)) return [option.value] // { title: {...}, value: "one" }
          if (has(option, PROP_TITLE)) {
            // HADS-style: title is a plain string ("Vaak"). Older shape: title is { nl, en, ... }.
            return isString(option.title) ? [option.title] : _.values(option.title)
          }
          if (isString(option)) return [option] // "one"
          return _.values(option) // { nl: "Een", en: "One" }
        }

        // Resolve the human-readable label for a chosen option, given the active locale.
        const getOptionLabel = (option) => {
          if (option == null) return option
          if (isString(option)) return option
          if (has(option, PROP_TITLE)) {
            return localized(option.title, locale)
          }
          return localized(option, locale)
        }

        const isOptionTheChosenAnswer = (option) => {
          const optionValues = getOptionMatchValues(option)
          const answeredValue = _.get(questionnaireData.values, question.id)
          // Accept either strict equality, or a case-insensitive string comparison so that HADS-style
          // payloads (option title "Vaak" vs. answer "vaak") and numeric/string score mismatches
          // (option.value 0 vs. answer "0") still resolve to the matching option.
          return optionValues.some((v) => {
            if (v === answeredValue) return true
            if (v == null || answeredValue == null) return false
            return String(v).toLowerCase() === String(answeredValue).toLowerCase()
          })
        }

        const chosenOption = _.find(extendedQuestionOptions, isOptionTheChosenAnswer)
        return _.capitalize(getOptionLabel(chosenOption))
      }
      case QUESTION_TYPE_DROPDOWN: {
        const rawAnswer = _.get(questionnaireData.values, question.id)
        const answerOption = _.find(question.options, { [questionnaireData.values.locale]: rawAnswer })
        const translatedAnswer = localized(answerOption, locale) || rawAnswer
        return I18n.t(rawAnswer, { locale, defaultValue: translatedAnswer })
      }
      default:
        return _.get(questionnaireData.values, question.id)
    }
  }

  const results = []

  questionnaireDefinition.content.questions.forEach((q) => {
    // The type expandable has to be handled uniquely.
    if (q.type === QUESTION_TYPE_EXPANDABLE) {
      const maxExpansions = q.max_expansions
      const questionTitle = localized(q.title, locale)
      const truncate = (text, max = 160) => {
        if (text == null) return text
        const str = String(text)
        return str.length > max ? `${str.slice(0, max)}…` : str
      }

      for (let i = 0; i < maxExpansions; i++) {
        q.content?.forEach(subQuestion => {
          let value
          if (subQuestion.type === QUESTION_TYPE_CHECKBOX && Array.isArray(subQuestion.options)) {
            const labels = []
            subQuestion.options.forEach((opt) => {
              const elemId = buildCheckboxElemId(subQuestion.id, opt)
              const dynamicKey = expandSubQuestionIdExpandable(elemId, i)
              const truthy = _.get(questionnaireData.values, dynamicKey)
              if (truthy) {
                const labelObj = _.get(opt, PROP_TITLE, opt)
                const label = localized(labelObj, locale)
                labels.push(label)
              }
            })
            // Handle otherwise checkbox in expandables, this is currently not being used, but kept for the future cases.
            if (has(subQuestion, PROP_OTHERWISE_LABEL)) {
              const otherwiseId = buildCheckboxOtherwiseElemId(subQuestion.id, _.get(subQuestion, ['raw', PROP_OTHERWISE_LABEL], subQuestion.otherwise_label))
              const dynamicOtherwiseKey = expandSubQuestionIdExpandable(otherwiseId, i)
              if (_.get(questionnaireData.values, dynamicOtherwiseKey)) {
                labels.push(_.get(subQuestion, PROP_OTHERWISE_LABEL, DEFAULT_OTHER_LABEL))
              }
            }
            value = labels.join(LIST_SEPARATOR)
          } else {
            const dynamicKey = expandSubQuestionIdExpandable(subQuestion.id, i)
            value = _.get(questionnaireData.values, dynamicKey)
          }

          if (value !== null && value !== undefined && value !== '') {
            const subTitle = localized(subQuestion.title, locale) || subQuestion.id
            results.push({
              id: subQuestion.id,
              name: cleanupString(`${questionTitle} - Entry ${i + 1} - ${subTitle}`),
              value: cleanupString(truncate(value))
            })
          }
        })
      }

      return
    }

    const answer = _.get(questionnaireData.values, q.id)
    const hasAnswer = answer !== null && answer !== undefined && answer !== ''
    const hasTimeAnswer = _.has(questionnaireData.values, `${q.id}_uren`)

    let hasCheckboxSelections = false
    if (q.type === QUESTION_TYPE_CHECKBOX && Array.isArray(q.options)) {
      hasCheckboxSelections = q.options.some((opt) => {
        const elemId = buildCheckboxElemId(q.id, opt)
        return !!_.get(questionnaireData.values, elemId)
      }) || (has(q, PROP_OTHERWISE_LABEL) && !!_.get(questionnaireData.values, buildCheckboxOtherwiseElemId(q.id, _.get(q, ['raw', PROP_OTHERWISE_LABEL], q.otherwise_label))))
    }

    if (hasAnswer || hasTimeAnswer || hasCheckboxSelections) {
      const questionName = cleanupString(getQuestionName(q))
      const questionValue = cleanupString(getQuestionValue(q))
      results.push({ id: q.id, name: questionName, value: questionValue })
    }
  })

  return results
}

// Calculate the start date of a protocol subscription based on one of its response VDOs (using the included
// protocol_completion). The given `period` argument can be either 'weeks' or 'days', corresponding to whether
// the questionnaire is asked weekly or daily. If no start date could be found, it returns the current date (`moment()`).
// This function always returns a Moment object.
export const protocolSubscriptionStartDate = (questionnaireVdo, period) => {
  const nrCompletedMeasurements = questionnaireVdo.data_rows[0]?.protocol_completion?.filter(protComp => !protComp.future)?.length
  if (!nrCompletedMeasurements) return moment()
  // Assume that the questionnaire is asked once per period, so subtract from the current open_from datetime,
  // as many periods as there were measurements before the current measurement.
  return moment(questionnaireVdo.data_rows[0].open_from).subtract(nrCompletedMeasurements - 1, period)
}

// This function uses `protocolSubscriptionStartDate` to return the earliest start date of the protocol
// subscriptions found in the `questionnaireVdos`. This uses protocol_completions embedded in the
// questionnaire responses. Period is the period of the questionnaire and should be either 'weeks' or 'days'.
// If no start date could be found, the function returns null.
// Otherwise it returns a Date object.
export const earliestProtocolSubscriptionStartDate = (questionnaireVdos, period) => {
  let minDate = new Date()
  let flipped = false
  for (const questionnaireVdo of questionnaireVdos) {
    const protSubStartDate = protocolSubscriptionStartDate(questionnaireVdo, period)
    if (protSubStartDate.toDate() < minDate) {
      minDate = protSubStartDate.toDate()
      flipped = true
    }
  }
  return flipped ? minDate : null
}

export const filterOutTimings = (questionnaireValues) => {
  return _.pickBy(questionnaireValues, (value, key) => {
    return !key.match(/timing$/)
  })
}

// Helper function to extract expandable field data from questionnaire values
// Returns an array of objects, one for each filled iteration
export const readExpandableField = (questionnaireValues, expandableQuestion, locale = 'en') => {
  const expandableData = []
  const maxExpansions = expandableQuestion.max_expansions

  for (let i = 0; i < maxExpansions; i++) {
    const iterationData = {}
    let hasData = false

    expandableQuestion.content?.forEach(subQuestion => {
      const dynamicKey = `${expandableQuestion.id}_${i}_${subQuestion.id}`
      const value = questionnaireValues[dynamicKey]

      if (value !== null && value !== undefined && value !== '') {
        hasData = true
        const fieldName = localized(subQuestion.title, locale) || subQuestion.id
        iterationData[fieldName] = value
      }
    })

    if (hasData) {
      expandableData.push({
        iteration: i,
        data: iterationData
      })
    }
  }

  return expandableData
}

// Gets either an ostrc injury or illness object and returns whether the entry corresponds to a severe complaint or not
export const isSevereComplaint = (complaint) => (
  complaint.daysWithoutParticipation >= 1 ||
  complaint.trainingAdjustment === 100 ||
  complaint.participationDificulty === 100 ||
  complaint.performanceInfluence === 100
)
